import React, { useCallback, useEffect, useState } from "react";
import { QrCode, Smartphone, Link as LinkIcon, Clipboard } from "lucide-react";

export default function OperatorDashboard() {
  const [showScanner, setShowScanner] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const [scanError, setScanError] = useState(null);

  const supportsBarcodeDetector =
    typeof window !== "undefined" && "BarcodeDetector" in window;

  const onScanResult = useCallback((value) => {
    setLastCode(value || null);
    setShowScanner(false);

    if (value && /^https?:\/\//i.test(value)) {
      try {
        window.location.assign(value);
      } catch {
        /* noop */
      }
    }
  }, []);

  return (
    // ⬇️ margen izquierdo en desktop para no tapar el sidebar + centrado total
    <main className="lg:ml-64 min-h-[100svh] grid place-items-center p-4 md:p-6">
      <div className="w-full max-w-3xl space-y-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center gap-3">
            <Smartphone className="text-blue-600" />
            <h1 className="text-2xl font-semibold">Área de Operador</h1>
          </div>

          <p className="mt-3 text-gray-700">
            No tienes acceso a esta plataforma web. Para operar tu dispositivo,{" "}
            <b>escanea el código QR</b> del candado usando tu móvil.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded"
              onClick={() => {
                if (!supportsBarcodeDetector) {
                  setScanError(
                    "Tu navegador no soporta el escaneo directo. Abre la cámara de tu móvil y apunta al QR para seguir el enlace."
                  );
                  return;
                }
                setScanError(null);
                setShowScanner(true);
              }}
            >
              <QrCode size={18} /> Escanear QR
            </button>

            <p className="text-sm text-gray-500">
              Consejo: si el QR contiene un enlace, lo abriremos automáticamente.
            </p>
          </div>

          {scanError && (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {scanError}
            </div>
          )}

          {lastCode && (
            <div className="mt-5 border rounded p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-1">Último código leído</div>
              <div className="flex items-center justify-between gap-3">
                <code className="text-sm break-all">{lastCode}</code>
                <div className="flex items-center gap-2">
                  {/^https?:\/\//i.test(lastCode) ? (
                    <a
                      href={lastCode}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded border text-blue-600 hover:bg-blue-50"
                    >
                      <LinkIcon size={16} /> Abrir
                    </a>
                  ) : null}
                  <button
                    className="inline-flex items-center gap-1 px-3 py-1 rounded border"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(lastCode);
                      } catch {}
                    }}
                    title="Copiar al portapapeles"
                  >
                    <Clipboard size={16} /> Copiar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {showScanner && supportsBarcodeDetector && (
          <ScannerModal onClose={() => setShowScanner(false)} onResult={onScanResult} />
        )}
      </div>
    </main>
  );
}

function ScannerModal({ onClose, onResult }) {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const detectorRef = React.useRef(null);
  const [error, setError] = useState(null);

  const stopStream = React.useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    const BarcodeDetector = window.BarcodeDetector;
    async function start() {
      try {
        detectorRef.current = new BarcodeDetector({ formats: ["qr_code"] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const tick = async () => {
          try {
            if (videoRef.current && detectorRef.current) {
              const barcodes = await detectorRef.current.detect(videoRef.current);
              if (barcodes && barcodes.length > 0) {
                const value = barcodes[0].rawValue || "";
                stopStream();
                onResult && onResult(value);
                return;
              }
            }
          } catch {}
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setError(
          "No se pudo acceder a la cámara o el escaneo no está disponible en este dispositivo."
        );
      }
    }
    start();
    return () => stopStream();
  }, [onResult, stopStream]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="font-semibold">Escanear QR</div>
          <button onClick={onClose} className="px-3 py-1 rounded border">
            Cerrar
          </button>
        </div>
        <div className="p-4">
          {!error ? (
            <>
              <div className="rounded overflow-hidden bg-black">
                <video ref={videoRef} className="w-full h-[280px] object-cover" playsInline muted />
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Apunta la cámara al código QR del dispositivo.
              </p>
            </>
          ) : (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}